import React, { useEffect, useRef, useState } from "react";
import urls from "../../../../global/constants/UrlConstants";
import { getCallParams } from "../../../../utils/service";
import { getCallRecordings } from "./ShowAllService";

export let currentAudio: HTMLAudioElement | null = null;

type Props = {
  fileName?: string | null;
  recordingUrl?: string | null;
  storedRecordingUrl?: string | null;
  callLogId?: string | null;
  durationLabel?: string | null;
  recordingStatus?: string | null;
  recordingError?: string | null;
  recordingStoreStatus?: string | null;
  recordingStoreError?: string | null;
  autoPlay?: boolean;
};

const isHttpUrl = (value?: string | null) =>
  !!value && /^https?:\/\//i.test(String(value).trim());

/** Panel URLs like app.myoperator.co/audio/* redirect to login HTML — not playable. */
const isPlayableRecordingUrl = (value?: string | null) => {
  if (!isHttpUrl(value)) return false;
  const url = String(value).trim();
  if (/app\.myoperator\.(co|com)\/(?:users\/login|audio\/)/i.test(url)) {
    return false;
  }
  if (/\/users\/login/i.test(url)) return false;
  return true;
};

const isAudioMime = (mime?: string | null) =>
  !!mime && /audio|mpeg|mp3|wav|ogg|aac|x-m4a|octet-stream/i.test(mime);

const isNonAudioMime = (mime?: string | null) =>
  !!mime && /json|html|text\/plain|xml|javascript/i.test(mime);

const pickPlayableUrl = (payload: any): string => {
  if (!payload) return "";
  if (typeof payload === "string" && isPlayableRecordingUrl(payload)) {
    return payload.trim();
  }

  const candidates = [
    payload.url,
    payload.link,
    payload.recordingUrl,
    payload.recording_url,
    payload?.data?.url,
    payload?.data?.link,
    typeof payload?.data === "string" ? payload.data : null,
  ];

  for (const c of candidates) {
    if (typeof c === "string" && isPlayableRecordingUrl(c)) return c.trim();
  }

  if (Array.isArray(payload?.data)) {
    for (const item of payload.data) {
      if (typeof item === "string" && isPlayableRecordingUrl(item)) {
        return item.trim();
      }
      if (item && typeof item === "object") {
        const nested =
          item.url || item.link || item.recordingUrl || item.recording_url;
        if (typeof nested === "string" && isPlayableRecordingUrl(nested)) {
          return nested.trim();
        }
      }
    }
  }

  return "";
};

const friendlyRecordingError = (raw?: string) => {
  const msg = String(raw || "").trim();
  if (!msg) return "Recording unavailable";
  if (/no voice file found/i.test(msg) || /not found/i.test(msg)) {
    return "Recording not found or expired on MyOperator";
  }
  return msg;
};

const readErrorMessage = async (res: Response) => {
  try {
    const data = await res.clone().json();
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
  } catch {
    // ignore
  }
  return "";
};

const formatTime = (sec: number) => {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

const WaveBars = ({ playing }: { playing: boolean }) => (
  <span className="inline-flex h-4 items-end gap-[2px]" aria-hidden>
    {[0, 1, 2, 3].map((i) => (
      <span
        key={i}
        className="inline-block w-[2.5px] origin-bottom rounded-full bg-[#841C39]"
        style={{
          height: 14,
          transform: playing ? undefined : "scaleY(0.35)",
          animation: playing
            ? `yh-wave 0.9s ease-in-out ${i * 0.12}s infinite`
            : "none",
        }}
      />
    ))}
    <style>{`
      @keyframes yh-wave {
        0%, 100% { transform: scaleY(0.35); }
        50% { transform: scaleY(1); }
      }
    `}</style>
  </span>
);

const RecordingCell = ({
  fileName,
  recordingUrl,
  storedRecordingUrl,
  callLogId,
  durationLabel,
  recordingStatus,
  recordingError,
  recordingStoreStatus,
  recordingStoreError,
  autoPlay = false,
}: Props) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const statusHint = String(
    recordingStatus || recordingStoreStatus || ""
  ).toLowerCase();
  const storedReason = String(
    recordingError || recordingStoreError || ""
  ).trim();

  const hasRecording = !!(
    fileName ||
    callLogId ||
    isHttpUrl(recordingUrl) ||
    isPlayableRecordingUrl(storedRecordingUrl)
  );

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  useEffect(() => {
    if (!url || !shouldAutoPlay) return;
    const el = audioRef.current;
    if (!el) return;

    const tryPlay = async () => {
      try {
        if (currentAudio && currentAudio !== el) {
          currentAudio.pause();
          currentAudio.currentTime = 0;
        }
        currentAudio = el;
        el.load();
        await el.play();
        setPlaying(true);
      } catch (err: any) {
        setErrorMsg(err?.message || "Tap play to start");
        setPlaying(false);
      } finally {
        setShouldAutoPlay(false);
      }
    };

    tryPlay();
  }, [url, shouldAutoPlay]);

  const setPlayable = (nextUrl: string, asObjectUrl = false) => {
    if (asObjectUrl) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setObjectUrl(nextUrl);
    } else if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }
    setFailed(false);
    setErrorMsg("");
    setCurrent(0);
    setDuration(0);
    setUrl(nextUrl);
    setShouldAutoPlay(true);
  };

  const loadAndPlay = async () => {
    if (loading) return;
    setLoading(true);
    setFailed(false);
    setErrorMsg("");

    try {
      let lastError = "";

      if (isPlayableRecordingUrl(storedRecordingUrl)) {
        setPlayable(String(storedRecordingUrl).trim());
        return;
      }

      const streamQuery = new URLSearchParams();
      if (callLogId) streamQuery.set("id", String(callLogId));
      if (fileName) streamQuery.set("file", String(fileName));
      streamQuery.set("stream", "1");
      streamQuery.set("pull", "1");

      if (callLogId || fileName) {
        const callParams = await getCallParams("GET");
        const streamRes = await fetch(
          `${urls.RECORDINGURL}?${streamQuery.toString()}`,
          {
            method: "GET",
            headers: callParams.headers || {},
          }
        );

        if (streamRes.ok) {
          const contentType = streamRes.headers.get("content-type") || "";
          if (isAudioMime(contentType) && !isNonAudioMime(contentType)) {
            const blob = await streamRes.blob();
            if (blob.size > 0 && !isNonAudioMime(blob.type)) {
              setPlayable(URL.createObjectURL(blob), true);
              return;
            }
            lastError = "Empty audio stream";
          } else {
            lastError =
              (await readErrorMessage(streamRes)) ||
              "Stream response was not audio";
          }
        } else {
          lastError =
            (await readErrorMessage(streamRes)) ||
            `Stream failed (${streamRes.status})`;
        }
      }

      if (fileName || callLogId) {
        const callParams = await getCallParams("GET");
        const jsonQuery = new URLSearchParams();
        if (callLogId) jsonQuery.set("id", String(callLogId));
        if (fileName) jsonQuery.set("file", String(fileName));
        jsonQuery.set("pull", "1");
        const jsonRes = await fetch(
          `${urls.RECORDINGURL}?${jsonQuery.toString()}`,
          {
            method: "GET",
            headers: callParams.headers || {},
          }
        );
        const response = jsonRes.ok
          ? await jsonRes.json()
          : {
              success: 0,
              message: (await readErrorMessage(jsonRes)) || lastError,
            };

        if (response?.success === 0) {
          setFailed(true);
          setErrorMsg(
            friendlyRecordingError(
              response?.message || lastError || "Recording unavailable"
            )
          );
          return;
        }
        const playUrl = pickPlayableUrl(response);
        if (isPlayableRecordingUrl(playUrl)) {
          setPlayable(playUrl);
          return;
        }
        if (fileName) {
          const legacy = await getCallRecordings(fileName);
          const legacyUrl = pickPlayableUrl(legacy);
          if (isPlayableRecordingUrl(legacyUrl)) {
            setPlayable(legacyUrl);
            return;
          }
          if (legacy?.success === 0) {
            setFailed(true);
            setErrorMsg(
              friendlyRecordingError(
                legacy?.message || lastError || "Recording unavailable"
              )
            );
            return;
          }
        }
        setFailed(true);
        setErrorMsg(
          friendlyRecordingError(
            lastError ||
              response?.message ||
              "Recording not found or expired on MyOperator"
          )
        );
        return;
      }

      if (isPlayableRecordingUrl(recordingUrl)) {
        setPlayable(String(recordingUrl).trim());
        return;
      }

      setFailed(true);
      setErrorMsg(
        "Recording link is missing or expired (MyOperator panel links cannot be played directly)"
      );
    } catch (err: any) {
      setFailed(true);
      setErrorMsg(err?.message || "Failed to load recording");
    } finally {
      setLoading(false);
    }
  };

  const autoPlayed = useRef(false);
  useEffect(() => {
    if (!autoPlay || autoPlayed.current || !hasRecording) return;
    autoPlayed.current = true;
    loadAndPlay();
  }, [autoPlay, hasRecording]);

  const togglePlay = async () => {
    if (loading) return;
    const el = audioRef.current;
    if (!url) {
      await loadAndPlay();
      return;
    }
    if (!el) {
      await loadAndPlay();
      return;
    }
    try {
      if (currentAudio && currentAudio !== el) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      currentAudio = el;
      if (el.paused) {
        await el.play();
        setPlaying(true);
      } else {
        el.pause();
        setPlaying(false);
      }
    } catch (err: any) {
      setErrorMsg(err?.message || "Playback failed");
    }
  };

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = audioRef.current;
    const next = Number(e.target.value);
    setCurrent(next);
    if (el && url) el.currentTime = next;
  };

  if (!hasRecording) {
    const missingReason =
      storedReason ||
      (statusHint === "awaiting_webhook" || statusHint === "awaiting"
        ? "Recording webhook pending — will retry shortly"
        : statusHint === "failed"
          ? "Recording download failed"
          : statusHint === "missing" || statusHint === "skipped"
            ? "No recording available for this call"
            : "");
    if (missingReason) {
      return (
        <div className="flex min-w-[140px] max-w-[240px] flex-col gap-0.5">
          <span className="text-[11px] font-medium text-[#9CA3AF]">
            No recording
          </span>
          <span
            className="text-[10px] leading-snug text-[#DC2626]"
            title={missingReason}
          >
            {friendlyRecordingError(missingReason)}
          </span>
        </div>
      );
    }
    return <span className="text-[#9CA3AF]">—</span>;
  }

  const total =
    duration > 0
      ? duration
      : (() => {
          const m = String(durationLabel || "").match(/(\d+):(\d+)/);
          if (!m) return 0;
          return Number(m[1]) * 60 + Number(m[2]);
        })();
  const progress = total > 0 ? Math.min(100, (current / total) * 100) : 0;

  return (
    <div className="flex min-w-[220px] max-w-[280px] flex-col gap-1">
      {url ? (
        <audio
          key={url}
          ref={audioRef}
          preload="metadata"
          src={url}
          className="hidden"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setCurrent(0);
          }}
          onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (Number.isFinite(d)) setDuration(d);
          }}
          onError={() => {
            setFailed(true);
            setErrorMsg("Unsupported or expired recording — try again");
            setUrl(null);
            setPlaying(false);
          }}
        />
      ) : null}

      <div className="flex items-center gap-2 rounded-xl border border-[#E8D0D8] bg-gradient-to-r from-[#FFF8FA] to-[#F9F0F4] px-2 py-1.5 shadow-sm">
        <button
          type="button"
          onClick={togglePlay}
          disabled={loading}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#841C39] text-white transition hover:bg-[#6E1630] disabled:opacity-60"
          aria-label={playing ? "Pause" : "Play"}
          title={playing ? "Pause" : "Play"}
        >
          {loading ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : playing ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 5h4v14H6zm8 0h4v14h-4z" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between gap-2">
            <WaveBars playing={playing} />
            <span className="font-mono text-[10px] tabular-nums text-[#6B7280]">
              {formatTime(current)}
              <span className="text-[#C4B5B9]"> / </span>
              {formatTime(total || duration)}
            </span>
          </div>
          <div className="relative h-1.5 overflow-hidden rounded-full bg-[#E8D0D8]">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-[#841C39] transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
            <input
              type="range"
              min={0}
              max={total || 0}
              step={0.1}
              value={Math.min(current, total || 0)}
              onChange={onSeek}
              disabled={!url}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
              aria-label="Seek"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pl-1">
        {durationLabel ? (
          <span className="text-[11px] font-medium text-[#6B7280]">
            {durationLabel}
          </span>
        ) : null}
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            download={fileName || undefined}
            className="text-[11px] font-semibold text-[#841C39] hover:underline"
          >
            Open
          </a>
        ) : null}
        {failed ? (
          <button
            type="button"
            onClick={loadAndPlay}
            className="text-[11px] font-semibold text-[#DC2626] hover:underline"
          >
            Retry
          </button>
        ) : null}
      </div>
      {errorMsg ? (
        <span className="text-[10px] text-[#DC2626]">{errorMsg}</span>
      ) : null}
    </div>
  );
};

export default RecordingCell;
